import React, { useState } from 'react';

import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { TextBoxItemType } from '../../text-box';

interface TextItem {
  type: TextBoxItemType;
  contents: string;
  link?: string;
}

interface TextBoxEditorProps {
  texts?: TextItem[];
  textBoxGap?: string;
  onPropChange: (key: string, value: any) => void;
}

const TextBoxEditor: React.FC<TextBoxEditorProps> = ({
  texts = [],
  textBoxGap = '1rem',
  onPropChange,
}) => {
  const [newText, setNewText] = useState<TextItem>({
    type: 'body1',
    contents: '',
    link: '',
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<TextItem | null>(null);

  return (
    <>
      <Typography variant="h6">Content Options</Typography>
      <TextField
        label="Gap Between Elements"
        value={textBoxGap}
        onChange={(e) => onPropChange('textBoxGap', e.target.value)}
        fullWidth
        margin="normal"
        helperText="Spacing between text elements (e.g., 1rem, 16px, 2rem)"
      />
      <List sx={{ width: '100%', alignItems: 'left' }}>
        {texts.map((text, index) => (
          <ListItem key={index} component="div">
            <ListItemText
              primary={`${text.type}: ${text.contents}`}
              secondary={
                text.type === 'button' || text.type === 'primaryButton'
                  ? `Link: ${text.link || 'None'}`
                  : null
              }
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                const updatedTexts = [...texts];
                updatedTexts.splice(index, 1);
                onPropChange('texts', updatedTexts);
              }}
            >
              <DeleteOutline />
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setEditingIndex(index);
                setEditingText(text);
              }}
            >
              Edit Text
            </Button>
          </ListItem>
        ))}
      </List>

      {editingIndex !== null && editingText && (
        <>
          <Select
            value={editingText.type}
            onChange={(e) =>
              setEditingText({
                ...editingText,
                type: e.target.value as TextBoxItemType,
              })
            }
            fullWidth
          >
            <MenuItem value="h1">Heading 1</MenuItem>
            <MenuItem value="h2">Heading 2</MenuItem>
            <MenuItem value="h3">Heading 3</MenuItem>
            <MenuItem value="h4">Heading 4</MenuItem>
            <MenuItem value="h5">Heading 5</MenuItem>
            <MenuItem value="h6">Heading 6</MenuItem>
            <MenuItem value="body1">Body 1</MenuItem>
            <MenuItem value="body2">Body 2</MenuItem>
            <MenuItem value="primaryButton">Primary Button</MenuItem>
            <MenuItem value="secondaryButton">Secondary Button</MenuItem>
            <MenuItem value="textButton">Text Link</MenuItem>
            <MenuItem value="divider">Divider</MenuItem>
          </Select>
          {editingText.type !== 'divider' && (
            <TextField
              value={editingText.contents}
              onChange={(e) =>
                setEditingText({ ...editingText, contents: e.target.value })
              }
              placeholder="Enter text"
              fullWidth
              multiline={
                editingText.type !== 'button' &&
                editingText.type !== 'primaryButton' &&
                editingText.type !== 'secondaryButton' &&
                editingText.type !== 'textButton'
              }
              margin="normal"
            />
          )}
          {(editingText.type === 'button' ||
            editingText.type === 'primaryButton' ||
            editingText.type === 'secondaryButton' ||
            editingText.type === 'textButton') && (
            <TextField
              value={editingText.link || ''}
              onChange={(e) =>
                setEditingText({ ...editingText, link: e.target.value })
              }
              placeholder="Enter button link URL, # for internal links and * for scrolling to id"
              fullWidth
              margin="normal"
            />
          )}
          <Button
            onClick={() => {
              const updatedTexts = [...texts];
              updatedTexts[editingIndex] = editingText;
              onPropChange('texts', updatedTexts);
              setEditingIndex(null);
              setEditingText(null);
            }}
            variant="contained"
            color="primary"
          >
            Save Text
          </Button>
          <Button
            onClick={() => {
              setEditingIndex(null);
              setEditingText(null);
            }}
            variant="contained"
            color="secondary"
          >
            Cancel
          </Button>
        </>
      )}

      <Select
        value={newText.type}
        onChange={(e) =>
          setNewText({ ...newText, type: e.target.value as TextBoxItemType })
        }
        fullWidth
      >
        <MenuItem value="h1">Heading 1</MenuItem>
        <MenuItem value="h2">Heading 2</MenuItem>
        <MenuItem value="h3">Heading 3</MenuItem>
        <MenuItem value="h4">Heading 4</MenuItem>
        <MenuItem value="h5">Heading 5</MenuItem>
        <MenuItem value="h6">Heading 6</MenuItem>
        <MenuItem value="body1">Body 1</MenuItem>
        <MenuItem value="body2">Body 2</MenuItem>
        <MenuItem value="primaryButton">Primary Button</MenuItem>
        <MenuItem value="secondaryButton">Secondary Button</MenuItem>
        <MenuItem value="textButton">Text Link</MenuItem>
        <MenuItem value="divider">Divider</MenuItem>
      </Select>
      {newText.type !== 'divider' && (
        <TextField
          value={newText.contents}
          onChange={(e) => setNewText({ ...newText, contents: e.target.value })}
          placeholder="Enter text"
          fullWidth
          multiline={
            newText.type !== 'button' &&
            newText.type !== 'primaryButton' &&
            newText.type !== 'secondaryButton' &&
            newText.type !== 'textButton'
          }
          margin="normal"
        />
      )}
      {(newText.type === 'button' ||
        newText.type === 'primaryButton' ||
        newText.type === 'secondaryButton' ||
        newText.type === 'textButton') && (
        <TextField
          value={newText.link || ''}
          onChange={(e) => setNewText({ ...newText, link: e.target.value })}
          placeholder="Enter button link URL, # for internal links and * for scrolling to id"
          fullWidth
          margin="normal"
        />
      )}
      <Button
        onClick={() => {
          const updatedTexts = [...texts, newText];
          onPropChange('texts', updatedTexts);
          setNewText({ type: 'body1', contents: '', link: '' });
        }}
        variant="contained"
        color="primary"
      >
        <AddCircleOutline />
      </Button>
    </>
  );
};

export default TextBoxEditor;
